import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Alert,
  TextField
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Save as SaveIcon, Print as PrintIcon } from '@mui/icons-material';

function ShipmentDetail() {
  const { shipmentId } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  useEffect(() => {
    const fetchShipmentDetails = async () => {
      try {
        const shipmentsRef = collection(db, 'shipments');
        const q = query(shipmentsRef, where("ID Number", "==", shipmentId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const shipmentDoc = querySnapshot.docs[0];
          setShipment({ id: shipmentDoc.id, ...shipmentDoc.data() });
          setEditedData({ id: shipmentDoc.id, ...shipmentDoc.data() });
        } else {
          setError('Shipment record not found');
        }
      } catch (err) {
        console.error('Error fetching shipment details:', err);
        setError('Failed to load shipment details');
      } finally {
        setLoading(false);
      }
    };

    fetchShipmentDetails();
  }, [shipmentId]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      const shipmentRef = doc(db, 'shipments', shipment.id);
  
      // Create an object with valid field names from editedData
      const rawData = {
        "ID Number": editedData["ID Number"],
        "Collection Point": editedData["Collection Point"],
        "Specific Food Item": editedData["Specific Food Item"],
        "Wet Weight (g)": editedData["Wet Weight (g)"],
        "Wet Weight (lbs)": editedData["Wet Weight (lbs)"],
        "Dry weight (g)": editedData["Dry weight (g)"],
        "Collection Date": editedData["Collection Date"],
        "Dehydration Start Date": editedData["Dehydration Start Date"],
        "Dehydration End Date": editedData["Dehydration End Date"],
        "Grinding Date": editedData["Grinding Date"],
        "Next Step": editedData["Next Step"], // Include this if it's part of the form
        // Add other fields as needed
      };
  
      // Remove any fields that are undefined
      const updatedData = Object.fromEntries(
        Object.entries(rawData).filter(([_, value]) => value !== undefined)
      );
  
      await updateDoc(shipmentRef, updatedData);
      setShipment({ ...shipment, ...updatedData }); // Update local state with new data
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating shipment:', error);
      setError('Failed to save shipment details');
    }
  };
  


  const handleChange = (field, value) => {
    setEditedData(prevData => ({ ...prevData, [field]: value }));
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button component={Link} to="/shipments" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
          Back to Shipments List
        </Button>
      </Box>
    );
  }

  // Calculate the QR code value
  const qrCodeValue = `${window.location.origin}/shipments/${shipment['ID Number']}`;
  console.log("QR Code URL:", qrCodeValue);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button component={Link} to="/shipments" startIcon={<ArrowBackIcon />} sx={{ textTransform: 'none' }}>
          Back to Shipments List
        </Button>
        <Typography variant="h6">Shipment Details</Typography>
        {isEditing ? (
          <Button startIcon={<SaveIcon />} onClick={handleSaveClick} variant="contained">
            Save
          </Button>
        ) : (
          <Button startIcon={<EditIcon />} onClick={handleEditClick} variant="outlined">
            Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Grid container spacing={2}>
              {['ID Number', 'Collection Point', 'Specific Food Item', 'Status'].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <Typography variant="subtitle2" color="text.secondary">{field}</Typography>
                  {isEditing ? (
                    <TextField fullWidth value={editedData[field] || ''} onChange={(e) => handleChange(field, e.target.value)} />
                  ) : (
                    <Typography variant="body1">{shipment[field] || "N/A"}</Typography>
                  )}
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Weight Measurements</Typography>
            <Grid container spacing={2}>
              {['Wet Weight (g)', 'Wet Weight (lbs)', 'Dry weight (g)'].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <Typography variant="subtitle2" color="text.secondary">{field}</Typography>
                  {isEditing ? (
                    <TextField fullWidth value={editedData[field] || ''} onChange={(e) => handleChange(field, e.target.value)} />
                  ) : (
                    <Typography variant="body1">{shipment[field] || "N/A"} {field.includes('Weight (lbs)') ? 'lbs' : 'g'}</Typography>
                  )}
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Dates</Typography>
            <Grid container spacing={2}>
              {['Collection Date', 'Dehydration Start Date', 'Dehydration End Date', 'Grinding Date'].map((field) => (
                <Grid item xs={12} sm={6} key={field}>
                  <Typography variant="subtitle2" color="text.secondary">{field}</Typography>
                  {isEditing ? (
                    <TextField fullWidth value={editedData[field] || ''} onChange={(e) => handleChange(field, e.target.value)} />
                  ) : (
                    <Typography variant="body1">{shipment[field] || "N/A"}</Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>QR Code</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <QRCodeCanvas value={qrCodeValue} size={200} />
            </Box>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mt: 2 }}
            >
              Print
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ShipmentDetail;
